/**
 * runShutdownEvidence (NC-1289)
 *
 * Answers the top beta question — "wurde die VM auch abgeschaltet?" — from the
 * evidence that is actually persisted on the run document, without a diagnostics
 * round-trip.
 *
 * Which fields exist (verified against the API types, because the issue named a
 * field that does not exist):
 *
 *   stoppedAtUtc   set by 150-infra-stop.ps1 / shared/stopService.ts, and ONLY
 *                  when every VM of the run deallocated cleanly. That makes it
 *                  stronger evidence than a momentary power-state poll.
 *   deletedAtUtc   set when cleanup is DISPATCHED (runs-delete, runs-cleanup-due).
 *                  Cleanup requested — not yet proven finished.
 *   expiredAtUtc   set by 160-infra-cleanup.ps1 once the resource group is gone.
 *                  This is the "actually cleaned up" proof.
 *
 * `resourcesDeletedUtc` from the issue's IST section does NOT exist on Run —
 * see the note in web-portal/api/shared/runRuntime.ts:117.
 *
 * The optional `liveVms` argument carries a power-state snapshot when a caller
 * already has one (RunDiagnosticsPanel's payload). Nothing fetches it just for
 * this badge: that would add an ARM-backed call to every run-detail page view.
 * When it is absent the badge falls back to persisted state, which is the point.
 */

import { Run } from '../services/types';

export type ShutdownEvidenceState =
  | 'cleaned'          // resource group deleted (expiredAtUtc)
  | 'cleanup-pending'  // cleanup dispatched, completion not yet recorded
  | 'stopped'          // all VMs deallocated (stoppedAtUtc)
  | 'running'          // VMs exist and nothing says they were stopped
  | 'no-vms'           // before step 130 — nothing to stop yet
  | 'unknown';         // legacy run: VMs exist, no timestamps, not active

export interface ShutdownEvidence {
  state: ShutdownEvidenceState;
  label: string;
  /** MUI Chip colour. */
  color: 'success' | 'warning' | 'default';
  tooltip: string;
  /** ISO timestamp backing the label, when there is one. */
  at?: string;
  /** Live counters, only when a power-state snapshot was passed in. */
  running?: number;
  total?: number;
}

export interface LiveVmState {
  name: string;
  powerState: string | null;
}

const isRunning = (powerState: string | null | undefined): boolean =>
  !!powerState && powerState.toLowerCase().includes('running');

/** Number of VMs attached to the run (accounts that reached step 130). */
export function countRunVms(run: Pick<Run, 'accounts'>): number {
  return run.accounts?.filter((acc) => acc.vm?.vmName).length ?? 0;
}

export function resolveShutdownEvidence(
  run: Pick<Run, 'accounts' | 'status' | 'stoppedAtUtc' | 'expiredAtUtc' | 'deletedAtUtc'>,
  liveVms?: LiveVmState[]
): ShutdownEvidence {
  // Cleanup outcomes win: once the resource group is gone, the VM question is moot.
  if (run.expiredAtUtc) {
    return {
      state: 'cleaned',
      label: 'Aufgeraeumt',
      color: 'default',
      at: run.expiredAtUtc,
      tooltip:
        'Die Ressourcengruppe des Laufs wurde geloescht (expiredAtUtc, gesetzt von 160-infra-cleanup.ps1). Es laufen keine Ressourcen mehr.',
    };
  }

  if (run.deletedAtUtc) {
    return {
      state: 'cleanup-pending',
      label: 'Aufraeumen angestossen',
      color: 'warning',
      at: run.deletedAtUtc,
      tooltip:
        'Das Aufraeumen wurde angestossen (deletedAtUtc), der Abschluss ist aber noch nicht bestaetigt — erst expiredAtUtc belegt, dass die Ressourcengruppe weg ist.',
    };
  }

  const total = countRunVms(run);

  // Nothing provisioned yet — must not read as "gestoppt" (AC 1).
  if (total === 0) {
    return {
      state: 'no-vms',
      label: 'Keine VMs',
      color: 'default',
      tooltip:
        'Fuer diesen Lauf existieren noch keine VMs (Schritt 130 nicht gelaufen) — es gibt nichts abzuschalten.',
    };
  }

  // A live snapshot, when the caller has one, outranks the persisted stop mark:
  // a VM restarted after the stop would otherwise still show green (AC 2).
  if (liveVms && liveVms.length > 0) {
    const running = liveVms.filter((vm) => isRunning(vm.powerState)).length;
    if (running > 0) {
      return {
        state: 'running',
        label: `VMs laufen (${running}/${liveVms.length})`,
        color: 'warning',
        running,
        total: liveVms.length,
        tooltip:
          `${running} von ${liveVms.length} VMs melden PowerState "running". Kosten laufen weiter, bis alle deallokiert sind.`,
      };
    }
    return {
      state: 'stopped',
      label: 'VMs gestoppt',
      color: 'success',
      at: run.stoppedAtUtc,
      running: 0,
      total: liveVms.length,
      tooltip:
        `Alle ${liveVms.length} VMs melden einen nicht-laufenden PowerState.` +
        (run.stoppedAtUtc ? '' : ' Ein Stop-Zeitstempel ist am Lauf allerdings nicht vermerkt.'),
    };
  }

  if (run.stoppedAtUtc) {
    return {
      state: 'stopped',
      label: 'VMs gestoppt',
      color: 'success',
      at: run.stoppedAtUtc,
      total,
      tooltip:
        'stoppedAtUtc wird nur gesetzt, wenn ALLE VMs des Laufs sauber deallokiert wurden — der Zeitstempel ist der Abschalt-Beweis. Wer den Stop ausgeloest hat (manuell, Auto-Stop, Abend-Runbook), steht nicht am Lauf, sondern im Audit-Log.',
    };
  }

  // VMs exist, no stop and no cleanup mark. An active run means they are up.
  if (run.status === 'active' || run.status === 'provisioning') {
    return {
      state: 'running',
      label: 'VMs laufen',
      color: 'warning',
      total,
      tooltip:
        `Der Lauf hat ${total} VM(s) und traegt keinen Stop-Zeitstempel — laut Lauf-Status "${run.status}" laufen sie. Abgeleitet aus dem gespeicherten Status, kein Live-Abruf.`,
    };
  }

  return {
    state: 'unknown',
    label: 'Kein Abschalt-Beweis',
    color: 'default',
    total,
    tooltip:
      `Der Lauf hat ${total} VM(s), aber weder Stop- noch Aufraeum-Zeitstempel (typisch fuer aeltere Laeufe von vor der Zeitstempel-Erfassung). Ob abgeschaltet wurde, ist aus dem Lauf-Dokument nicht belegbar — Diagnose-Tab oder Azure pruefen.`,
  };
}
